import { appStore } from '../mobx/appStore';
import { history } from './history';
import wms from './wms';
import { SCREENS } from 'src/constants/screens';
import { USER_PERMISSIONS } from 'src/constants/user';
import { userStore } from 'src/mobx/userStore';
import { hasPermissions } from 'src/utils/helpers';
import { localeStore } from '~/mobx/localesStore';
import logger from '~/services/logger';
import { removeToast, toastError } from '~/utils/toasts';

const soundNotificationToastId: string = 'sound-notification-toast-id';

const formatAudioPath = (name: string) => `${process.env.REACT_APP_CLOUD_FRONT || ''}/media/audio/${name}`;
const audioPath = formatAudioPath('notification.mp3');

class Player {
  constructor() {
    // Fix ERR_CACHE_OPERATION_NOT_SUPPORTED
    this.audio.onerror = this.audio.onabort = this.handleAudioLoadingError;
    this.audio.addEventListener('canplaythrough', this.handleAudioLoadingSuccess, false);
    this.audio.src = audioPath + `#${+new Date()}`;
    this.audio.className = 'stop';

    document.querySelector('body')?.appendChild(this.audio);
  }

  handleAudioLoadingSuccess = () => {
    if (this.isPlaying && this.timeout) {
      this.isPlaying = false;
      this.play(this.timeout);
    }
  };

  handleAudioLoadingError = () => {
    setTimeout(() => {
      this.audio.src = audioPath + `#${+new Date()}`;
    }, 5000);
  };

  private timeout: number = 0;
  private timer: null | NodeJS.Timeout = null;
  private isPlaying: boolean = false;
  private audio: HTMLAudioElement = new Audio();
  /**
   * Indicates that sound notifications (on mobile Safari, maybe somewhere else) are allowed.
   * To do this, user must have interacted with page at least once.
   *
   * @private
   * @type {boolean}
   */
  private unlockMobileSafariAudio: boolean = false;
  /**
   * Indicates that sound notifications are allowed.
   * To do this, user must have interacted with page at least once.
   *
   * @private
   * @type {boolean}
   */
  private unlockBrowserAudio: boolean = false;

  public soundNotifications = () => {
    this.unlockMobileSafariAudioHandler();
    this.unlockBrowserAudioHandler();
  };

  public hideUnlockBrowserAudioMessage = () => {
    this.unlockBrowserAudioListener();
  };

  /**
   * For audio to work requires first by user action.
   * Shows a notification about need for user action, hangs a listener on user's action.
   */
  private unlockBrowserAudioHandler = () => {
    if (
      this.unlockBrowserAudio ||
      history.location.pathname === SCREENS.LOGIN() ||
      (history.location.pathname === SCREENS.ROOT() && !userStore.isLogin) ||
      !hasPermissions(USER_PERMISSIONS.ORDERS_MANAGMENT)
    ) {
      return;
    }

    toastError({
      id: soundNotificationToastId,
      title: localeStore.t('toast_header_browser_blocked_audio'),
      message: localeStore.t('toast_body_text_browser_blocked_audio'),
    });

    document.body.addEventListener('click', this.unlockBrowserAudioListener, { once: true });
  };
  private unlockBrowserAudioListener = () => {
    this.unlockBrowserAudio = true;
    removeToast(soundNotificationToastId);
  };

  /**
   * For audio to work in mobile safari (maybe somewhere else) requires first by user action.
   * Hangs a listener on user's action.
   */
  private unlockMobileSafariAudioHandler = () => {
    document.body.addEventListener('touchstart', this.unlockMobileSafariAudioListener, { once: true });
  };
  private unlockMobileSafariAudioListener = () => {
    if (this.unlockMobileSafariAudio) return;
    this.audio.play();
    this.audio.pause();
    this.audio.currentTime = 0;
    this.unlockMobileSafariAudio = true;
  };

  play = async (timeout: number = 0) => {
    try {
      if (window.IS_MOBILE) {
        wms.playSound(timeout);
        return;
      }
      if (this.isPlaying) return;
      if (this.timer) clearTimeout(this.timer);
      this.audio.loop = true;
      this.timeout = timeout;
      this.isPlaying = true;
      await this.audio.play();
      this.audio.className = 'play';
      appStore.changeSoundPlayingState(true);
      if (timeout) {
        this.timer = setTimeout(this.stop, timeout);
      }
    } catch (error: any) {
      logger.error({ error, place: 'Player play' });
    }
  };

  stop = () => {
    if (window.IS_MOBILE) {
      wms.playSound(0);
      return;
    }
    if (!this.isPlaying) return;
    if (this.timer) clearTimeout(this.timer);
    this.audio.pause();
    this.audio.className = 'pause';
    this.audio.currentTime = 0;
    this.isPlaying = false;
    this.timeout = 0;
    appStore.changeSoundPlayingState(false);
  };
}

const player = new Player();

export default player;
